import type { UseQueryResult } from '@tanstack/react-query';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import {
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components/SurfaceStates';
import { CleaningWeek } from '../calendar/CleaningWeek';
import { CleaningDayBoard } from '../day/CleaningDayBoard';
import type { CleaningDayFilter } from '../day/cleaningDayModel';
import type { useCleaningBoardQueries } from '../useCleaningBoardQueries';
import type { CleaningBoardView } from './boardModel';

const CARD = 'border-hairline rounded-tile overflow-hidden border bg-white';

/**
 * Liste oder Woche mit ihren Zuständen: lädt, keine Verbindung, Fehler mit Grund und Erneut laden,
 * veraltet (letzter Stand bleibt stehen), leer mit Datum.
 */
export function BoardBody({
  view,
  date,
  today,
  filter,
  queries,
  selectedId,
  onSelect,
  onClaim,
  claimingId,
  currentUserId,
  personFiltered,
  online,
}: {
  view: CleaningBoardView;
  date: string;
  today: string;
  filter: CleaningDayFilter;
  queries: ReturnType<typeof useCleaningBoardQueries>;
  selectedId: string | null;
  onSelect: (id: string, trigger: HTMLElement) => void;
  onClaim: (task: CleaningTaskQueueResponse) => void;
  claimingId: string | undefined;
  currentUserId: string | undefined;
  personFiltered: boolean;
  online: boolean;
}) {
  const week = view === 'calendar';
  const query: UseQueryResult<unknown[]> = week ? queries.weekQuery : queries.dayQuery;
  const problem = useApiProblem(query.error);
  const retry = () => void query.refetch();
  if (query.isPending && query.fetchStatus === 'paused')
    return (
      <p role="status" className={`${CARD} text-ink-soft p-5 text-sm`}>
        Keine Verbindung. {week ? 'Die Woche' : 'Die Liste'} lädt, sobald sie zurück ist.
      </p>
    );
  if (query.isPending)
    return (
      <div className={CARD}>
        <SurfaceLoading label={week ? 'Woche wird geladen' : 'Aufgaben werden geladen'} />
      </div>
    );
  if (!query.data)
    return (
      <div className={CARD}>
        <SurfaceLoadError
          message={problemText(
            problem,
            week
              ? 'Die Woche konnte nicht geladen werden.'
              : 'Die Aufgaben konnten nicht geladen werden.',
          )}
          onRetry={retry}
          retrying={query.isFetching}
        />
      </div>
    );
  return (
    <div className="flex flex-col gap-3">
      {query.isError && (
        <div className={CARD}>
          <SurfaceStaleHint onRetry={retry} retrying={query.isFetching} />
        </div>
      )}
      {!online && (
        <p
          role="status"
          className="bg-amber-tint text-amber-ink rounded-tile p-3 text-[13px] font-semibold"
        >
          Keine Verbindung – zu sehen ist der zuletzt geladene Stand. Übernehmen geht erst wieder
          mit Netz.
        </p>
      )}
      {week ? (
        queries.rows.length ? (
          <CleaningWeek
            rows={queries.rows}
            weekDates={queries.weekDates}
            date={date}
            today={today}
            selectedId={selectedId}
            onSelect={(task, trigger) => onSelect(task.id, trigger)}
          />
        ) : (
          <p className={`${CARD} text-ink-soft p-5 text-sm`}>In dieser Woche keine Reinigungen.</p>
        )
      ) : (
        <CleaningDayBoard
          tasks={queries.dayTasks}
          date={date}
          filter={filter}
          personFiltered={personFiltered}
          offline={!online}
          selectedId={selectedId}
          onSelect={(task, trigger) => onSelect(task.id, trigger)}
          onClaim={onClaim}
          claimingId={claimingId}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
}
